import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useUserStore } from "store/userStore";
import { useStore } from "store/zustand";
import { getAuth, signOut } from "firebase/auth";
import { useCreditStore } from "store/creditStore";
import { getOpenRequestCount } from "apis/requestServices";
import CreditPurchaseModal from "pages/Credits/CreditPurchaseModal";
import TokensIcon from "assets/images/tokens.png";
import StandbyLogo from "assets/images/standby.png";
import LdActive from "assets/images/ld-active.png";
import Ld from "assets/images/ld.png";
import YrqActive from "assets/images/yrq-active.png";
import Yrq from "assets/images/yrq.png";
import ReqIcon from "assets/images/req.png";
import OpenActive from "assets/images/open-active.png";
import OpenIcon from "assets/images/open.png";
import AnsweredActive from "assets/images/answered-active.png";
import AnsweredIcon from "assets/images/answered.png";
import TokenIcon from "assets/images/token.png";
import TokenActiveIcon from "assets/images/token-active.png";
import LiveTrackingIcon from "assets/images/LiveTracking.png";
import LiveTrackingActiveIcon from "assets/images/LiveTracking-1.png";
import { isAdminRole, isUserSideRole } from "utils/roles";

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userRole = useUserStore((state) => state.role);
  const { logout } = useStore();

  const [isRequestsOpen, setIsRequestsOpen] = useState(false);
  const [openRequestCount, setOpenRequestCount] = useState(0);
  const availableCredits = useCreditStore((s) => s.availableCredits);
  const refreshCredits = useCreditStore((s) => s.refresh);
  const [showCreditPurchaseModal, setShowCreditPurchaseModal] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // Auto-expand Requests submenu if on a request-related page
  useEffect(() => {
    if (location.pathname === "/my-requests") {
      setIsRequestsOpen(true);
    }
  }, [location.pathname]);

  // Close mobile sidebar on any navigation. Keyed on location.key (not just
  // pathname) so it also fires for Open ↔ Answered, which share the
  // /my-requests path and differ only by location.state.tab.
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.key]);

  useEffect(() => {
    if (isUserSideRole(userRole)) {
      refreshCredits();
    }
  }, [userRole]);

  // Keep the open-request count badge fresh. Re-fetch on navigation so it
  // updates after a request is answered elsewhere in the app.
  useEffect(() => {
    if (!isUserSideRole(userRole)) return;
    let active = true;
    getOpenRequestCount()
      .then((res: any) => {
        // API returns { Count: <number> } (serialized as Count/count).
        const count = res?.Count ?? res?.count ?? res ?? 0;
        if (active) setOpenRequestCount(Number(count) || 0);
      })
      .catch(() => {
        /* badge is non-critical; ignore fetch errors */
      });
    return () => {
      active = false;
    };
  }, [userRole, location.pathname]);

  const logoutFunction = async () => {
    const auth = getAuth();
    try {
      await signOut(auth);
      logout();
      // Reset the whole user store, not just the role — the in-memory state
      // outlives this client-side navigation, so anything left behind (e.g.
      // the profile picture) would show up for the next user who logs in.
      useUserStore.getState().resetUserDetails();
      localStorage.removeItem("userDetails");
      localStorage.removeItem("token");
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-all duration-200 ${
      isActive
        ? "text-[#1F2937] font-semibold"
        : "text-[#4B5563] hover:bg-[#F3F4F6] hover:text-[#1F2937] font-medium"
    }`;

  const subNavLinkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 pl-11 pr-4 py-2.5 rounded-xl text-sm transition-all duration-200 ${
      isActive
        ? "bg-[#FEF3C7] text-[#D97706] font-medium"
        : "text-[#6B7280] hover:bg-[#F3F4F6] hover:text-[#4B5563]"
    }`;

  const adminRoutes = [
    { to: "/admin/dashboard", icon: "st-dashboard", label: "Dashboard" },
    { to: "/admin/user-management", icon: "st-user", label: "User Management" },
    {
      to: "/admin/credit-management",
      icon: "st-credits",
      label: "Token Management",
    },
    {
      to: "/admin/report&feedback",
      icon: "st-comment",
      label: "Report & Feedback",
    },
    {
      to: "/admin/discount-management",
      icon: "st-discount",
      label: "Discount Management",
    },
    {
      to: "/admin/non-rev-config",
      icon: "st-settings",
      label: "Non-Rev Config",
    },
    { to: "/admin/settings", icon: "st-settings", label: "Settings" },
  ];

  return (
    <>
      {/* Mobile toggle button */}
      <button
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        className="fixed top-4 left-4 z-50 md:hidden bg-white p-2 rounded-lg shadow-lg"
        type="button"
      >
        <svg
          className="w-6 h-6 text-gray-700"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          {isMobileOpen ? (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          ) : (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          )}
        </svg>
      </button>

      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40 md:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-[250px] bg-white border-r border-[#E5E7EB] flex flex-col z-40 transition-transform duration-300 ${
          isMobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
      >
        {/* Logo */}
        <div className="px-6 py-5 border-b border-[#F3F4F6]">
          <NavLink to="/search" className="flex items-center gap-2">
            <img
              src={StandbyLogo}
              alt="Standby"
              className="h-8 w-auto object-contain"
            />
            <span className="text-[#1F2937] font-bold text-xl tracking-tight">
              STANDBY
            </span>
          </NavLink>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-4 py-4">
          {isAdminRole(userRole) ? (
            <>
              <p className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider px-4 mb-3">
                Dashboard
              </p>
              <div className="space-y-1">
                {adminRoutes.map(({ to, icon, label }) => (
                  <NavLink key={to} to={to} className={navLinkClass}>
                    <i className={`${icon} text-lg`}></i>
                    <span>{label}</span>
                  </NavLink>
                ))}
              </div>
            </>
          ) : (
            <>
              <p className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider px-4 mb-3">
                Dashboard
              </p>
              <div className="space-y-1">
                {/* Loads */}
                <NavLink to="/search" className={navLinkClass} end={false}>
                  {({ isActive }) => (
                    <>
                      <img
                        src={isActive ? LdActive : Ld}
                        alt="Loads"
                        className="w-5 h-5"
                      />
                      <span>Loads</span>
                    </>
                  )}
                </NavLink>

                {/* Your Requests */}
                <NavLink to="/my-loads" className={navLinkClass}>
                  {({ isActive }) => (
                    <>
                      <img
                        src={isActive ? YrqActive : Yrq}
                        alt="Your Requests"
                        className="w-5 h-5"
                      />
                      <span>Your Requests</span>
                    </>
                  )}
                </NavLink>

                {/* Requests (collapsible) */}
                <div>
                  <button
                    onClick={() => {
                      setIsRequestsOpen(true);
                      navigate("/my-requests", { state: { tab: "open" } });
                    }}
                    className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl text-sm transition-all duration-200 ${
                      location.pathname === "/my-requests"
                        ? "text-[#1F2937] font-semibold"
                        : "text-[#4B5563] hover:bg-[#F3F4F6] hover:text-[#1F2937] font-medium"
                    }`}
                    type="button"
                  >
                    <div className="flex items-center gap-3">
                      <img src={ReqIcon} alt="Requests" className="w-5 h-5" />
                      <span>Requests</span>
                      {openRequestCount > 0 && (
                        <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold rounded-full bg-[#FBBF24] text-white">
                          {openRequestCount}
                        </span>
                      )}
                    </div>
                    <svg
                      className={`w-4 h-4 transition-transform duration-200 ${isRequestsOpen ? "rotate-180" : ""} ${
                        location.pathname === "/my-requests"
                          ? "text-white"
                          : "text-[#6B7280]"
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {isRequestsOpen && (
                    <div className="mt-1 space-y-0.5">
                      <NavLink
                        to="/my-requests"
                        state={{ tab: "open" }}
                        className={({ isActive }) =>
                          `flex items-center gap-3 pl-8 pr-4 py-2.5 rounded-xl text-sm transition-all duration-200 ${
                            isActive && location.state?.tab !== "answered"
                              ? "text-[#1F2937] font-semibold"
                              : "text-[#6B7280] hover:bg-[#F3F4F6] hover:text-[#4B5563]"
                          }`
                        }
                      >
                        {({ isActive }) => (
                          <>
                            <img
                              src={
                                isActive && location.state?.tab !== "answered"
                                  ? OpenActive
                                  : OpenIcon
                              }
                              alt="Open"
                              className="w-4 h-4"
                            />
                            <span>Open</span>
                          </>
                        )}
                      </NavLink>
                      <NavLink
                        to="/my-requests"
                        state={{ tab: "answered" }}
                        className={({ isActive }) =>
                          `flex items-center gap-3 pl-8 pr-4 py-2.5 rounded-xl text-sm transition-all duration-200 ${
                            isActive && location.state?.tab === "answered"
                              ? "text-[#1F2937] font-semibold"
                              : "text-[#6B7280] hover:bg-[#F3F4F6] hover:text-[#4B5563]"
                          }`
                        }
                      >
                        {({ isActive }) => (
                          <>
                            <img
                              src={
                                isActive && location.state?.tab === "answered"
                                  ? AnsweredActive
                                  : AnsweredIcon
                              }
                              alt="Answered"
                              className="w-4 h-4"
                            />
                            <span>Answered</span>
                          </>
                        )}
                      </NavLink>
                    </div>
                  )}
                </div>

                {/* Live Tracking */}
                <NavLink to="/live-tracking" className={navLinkClass}>
                  {({ isActive }) => (
                    <>
                      <img
                        src={
                          isActive ? LiveTrackingActiveIcon : LiveTrackingIcon
                        }
                        alt="Live Tracking"
                        className="w-5 h-5"
                      />
                      <span>Live Tracking</span>
                    </>
                  )}
                </NavLink>

                {/* Deals
                <NavLink to="/deals" className={navLinkClass}>
                  {({ isActive }) => (
                    <>
                      <svg
                        className={`w-5 h-5 ${isActive ? "text-[#1F2937]" : "text-[#9CA3AF]"}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 7h.01M7 3h5a1.99 1.99 0 011.414.586l7 7a2 2 0 010 2.828l-5 5a2 2 0 01-2.828 0l-7-7A2 2 0 014 7V4a1 1 0 011-1z"
                        />
                      </svg>
                      <span>Deals</span>
                    </>
                  )}
                </NavLink> */}

                {/* Tokens */}
                <NavLink to="/credits" className={navLinkClass}>
                  {({ isActive }) => (
                    <>
                      <img
                        src={isActive ? TokenActiveIcon : TokenIcon}
                        alt="Tokens"
                        className="w-5 h-5"
                      />
                      <span>Tokens</span>
                    </>
                  )}
                </NavLink>
              </div>
            </>
          )}
        </nav>

        {/* Bottom Section */}
        <div className="border-t border-[#F3F4F6] px-8 py-4 space-y-2">
          {/* Token Balance Card - only for user role, hidden on credits page */}
          {isUserSideRole(userRole) && location.pathname !== "/credits" && (
            <div className="bg-gradient-to-br from-[#1F2937] to-[#111827] rounded-2xl p-4 mb-3">
              <div className="flex items-center gap-3 mb-2">
                <img
                  src={TokensIcon}
                  alt="Tokens"
                  className="w-10 h-10 object-contain"
                />
                <div>
                  <p className="text-white text-2xl font-bold leading-none">
                    {availableCredits == null ? "..." : availableCredits}
                  </p>
                  <p className="text-white/60 text-[11px] mt-0.5">
                    Total Tokens Earned
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowCreditPurchaseModal(true)}
                className="w-full bg-[#FBBF24] text-white text-sm font-semibold py-2.5 rounded-xl hover:bg-[#F59E0B] transition-colors"
              >
                Purchase Tokens
              </button>
            </div>
          )}

          {/* Settings */}
          {isUserSideRole(userRole) && (
            <NavLink
              to="/settings"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-all duration-200 ${
                  isActive
                    ? "bg-[#F3F4F6] text-[#1F2937] font-medium"
                    : "text-[#4B5563] hover:bg-[#F3F4F6] hover:text-[#1F2937]"
                }`
              }
            >
              <svg
                className="w-5 h-5 text-[#6B7280]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              <span>Settings</span>
            </NavLink>
          )}

          {/* Logout */}
          <button
            onClick={logoutFunction}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-[#4B5563] hover:bg-red-50 hover:text-red-600 transition-all duration-200"
            type="button"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            <span>Log out</span>
          </button>
        </div>
      </aside>

      {/* Credit Purchase Modal */}
      <CreditPurchaseModal
        onPaymentComplete={refreshCredits}
        isOpen={showCreditPurchaseModal}
        onClose={() => setShowCreditPurchaseModal(false)}
      />
    </>
  );
};

export default Sidebar;
